/**
 * The three grupos, as permission data — until `GET /eu` serves the real ones.
 *
 * ⚠️ **NOTHING HERE IS INVENTED.** Every row is transcribed from the persona ×
 * screen matrix in `docs/PERSONAS.md`: `✎` → `escrita`, `👁` → `leitura`, `—` →
 * omitted (which resolves to `{ver:false, editar:false}`). If a screen's access
 * looks wrong, the matrix is the thing to argue with, not this file.
 *
 * The keys are the `permissao` fields already declared in `app/navigation.ts`, so
 * the navigation model and the permission model cannot drift apart.
 *
 * 🔒 **`Grupo` carries no client reference** — BR-18. The same grupo applies to
 * every client the user reaches. That consequence is still open with iOX (§9);
 * do not build anything that depends on the opposite.
 */
import type { Grupo } from '@/permissions/resolver'

/**
 * Screens the matrix marks `👁` for **everyone**. Pulled out because repeating
 * them three times invites one copy to drift.
 */
const LEITURA_COMUM = [
  'finops',
  'finops.dashboard',
  'finops.custos',
  'orcamento',
  'orcamento.dashboard',
  'compliance',
  'admin.configuracoes',
] as const

/**
 * Compliance v4 — as quatro folhas que as personas IOX (Administrador e Arquiteto)
 * escrevem. Contrato compartilhado de 16/09/2026.
 */
const COMPLIANCE_IOX = [
  'compliance.perguntas',
  'compliance.questionario',
  'compliance.documentos',
  'compliance.notas_ia',
] as const

function leitura(...chaves: readonly string[]) {
  return chaves.map((idFuncionalidade) => ({ idFuncionalidade, nivel: 'leitura' as const }))
}

function escrita(...chaves: readonly string[]) {
  return chaves.map((idFuncionalidade) => ({ idFuncionalidade, nivel: 'escrita' as const }))
}

export const GRUPOS_FIXTURE: Grupo[] = [
  {
    idGrupo: 'administrador',
    nome: 'Administrador',
    permissoes: [
      ...leitura(...LEITURA_COMUM),
      // "Mostly outside the client scope" — the Administrador READS the client
      // modules and WRITES the platform ones. The matrix is 👁 across FinOps and
      // Orçamento for them, which surprises people; it is deliberate.
      ...leitura(
        'finops.discovery',
        'finops.oportunidades',
        'finops.configuracoes',
        'orcamento.contratos',
        'orcamento.faturas',
      ),
      ...escrita(
        'admin',
        'admin.clientes',
        'admin.usuarios',
        'admin.perfis',
        'admin.configuracoes',
        // Fase 4 do roadmap de IA (`V0021`) — escrita pros dois papéis de
        // engenharia de prompt, sem linha pro Cliente (mesmo padrão BR-23 de
        // `compliance.perguntas`: a ausência da linha É a regra).
        'admin.materiais_ia',
      ),
      // Compliance v4 — decisão do Fábio em 16/09/2026: Administrador e Arquiteto
      // podem TUDO em Compliance, incluindo o catálogo global (`3a`/`3f`, BR-23).
      ...escrita(...COMPLIANCE_IOX),
    ],
  },
  {
    idGrupo: 'arquiteto-iox',
    nome: 'Arquiteto Cloud',
    permissoes: [
      ...leitura(...LEITURA_COMUM),
      ...leitura('orcamento.faturas'),
      // Compliance v4 (16/09/2026): mesmo acesso do Administrador — tudo.
      ...escrita(...COMPLIANCE_IOX),
      // Fase 4 do roadmap de IA (`V0021`) — mesma concessão do Administrador.
      ...escrita('admin.materiais_ia'),
      // The FinOps lifecycle is this persona's working surface — and they are the
      // ONLY writer on the Oportunidades grid (`2e`), where the "visível ao cliente"
      // toggle of BR-01 lives.
      ...escrita('finops.discovery', 'finops.oportunidades', 'finops.configuracoes'),
      /**
       * 🔴 **Corrigido em 2026-08-23: era `leitura`, e a matriz sempre disse `✎`.**
       *
       * `PERSONAS.md` marca *Contratos & aditivos* (`2j`) como **✎ para o
       * Arquiteto**, e `PERSONAS-ref.md` dá a ele **CRUD** em *Cadastrar
       * contrato*, *Cadastrar aditivo* e *Mês fiscal · PTAX · % desconto*. A
       * transcrição original juntou esta chave às de leitura por engano.
       *
       * A consequência era silenciosa e total: **ninguém** podia escrever em
       * `orcamento.contratos` — o Cliente por decisão (§9, 12/08), o
       * Administrador por ser 👁 na matriz, e o Arquiteto por este erro. Ou
       * seja, os controles que o shot `2j` desenha (`novo contrato`, `salvar
       * parâmetros`, `anexar aditivo`) eram inalcançáveis por qualquer persona,
       * e nada apontava para isso porque nenhum teste cobria esta linha.
       *
       * É exatamente o tipo de drift que o cabeçalho deste arquivo alerta. Agora
       * tem teste.
       */
      ...escrita('orcamento.contratos'),
    ],
  },
  {
    idGrupo: 'cliente',
    nome: 'Cliente',
    permissoes: [
      ...leitura(...LEITURA_COMUM),
      // ¹ The RAW Discovery layer may be visible — only the CURATED opportunity is
      // gated. Eduardo: "não vejo problema do cliente ter acesso a isso que não foi
      // avaliado ainda."
      ...leitura('finops.discovery'),
      /**
       * ⚠️ `orcamento.contratos` is LEITURA, not escrita.
       *
       * `PERSONAS.md` still marks this `✎†`, where the `†` records Eduardo's
       * *"a gente tem visibilidade e editar, **acho que sim**"*. §9 of
       * PROJECT-OVERVIEW **closed it as read-only on 2026-08-12**, and AC-3 of this
       * story agrees. §9 wins; the matrix row is stale and is corrected as part of
       * story 0.4.
       */
      ...leitura('orcamento.contratos'),
      // A real author in Contratual and Compliance — reconciling the broker invoice,
      // filling the questionnaire (`3b`, `3d`) and sending evidence (`3c`).
      ...escrita('orcamento.faturas', 'compliance.questionario', 'compliance.documentos'),
      // Compliance v4 (16/09/2026): vê a "Nota para IA" e não a edita — é o retorno
      // do arquiteto IOX sobre o parecer da IA. `compliance.perguntas` (`3a`) é
      // AUSENTE: a aba não existe para o Cliente.
      ...leitura('compliance.notas_ia'),
      // ² `finops.oportunidades` (2e) is DELIBERATELY ABSENT. The Cliente's view is
      // the dashboard publicado (2g), filtered server-side by `visibilidadeCliente`
      // (BR-01/BR-02). Granting it here would put the curation surface — including
      // the visibility toggle — in the client's hands.
    ],
  },
]

/** The grupos a session's `grupos: string[]` refers to. Unknown ids are ignored. */
export function gruposPorId(ids: readonly string[]): Grupo[] {
  return GRUPOS_FIXTURE.filter((grupo) => ids.includes(grupo.idGrupo))
}
